// RecipeGenerator.jsx
import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { Card, Form, Button, Spinner, Alert, Row, Col, ToggleButton, ToggleButtonGroup, Badge, InputGroup, Tooltip, OverlayTrigger, CardImg } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faUtensils, faSeedling, faUsers, faInfoCircle, faBullseye, faFire,
    faGlobeAmericas, faBan, faTimes, faLightbulb, faHistory, faPlusCircle,
    faSave, faCheckCircle, faSearch, faEye, faArrowLeft // Añadido faArrowLeft
} from '@fortawesome/free-solid-svg-icons';

import { getRecipeHistory, hideUserActivity, visibleUserActivity } from '../services/dbService';
import { callPollinationsTextApi } from '../services/aiService';

import ViewModeLayout from './ui/ViewModeLayout';
import HistoryView from './ui/HistoryView';
import ItemDetailsModal from './ui/ItemDetailsModal';
import RecipeDisplay from './ui/RecipeDisplay';
import PreferenceForm from './ui/PreferenceForm.jsx';

const IMAGE_API_BASE_POLLINATIONS = 'https://image.pollinations.ai/prompt/';
const DEFAULT_IMAGE_MODEL = 'flux';

const RecipeGenerator = ({ userId, userRestrictions = [], userHealthGoals = [], onRecipeGenerated }) => {
    const [viewMode, setViewMode] = useState('history');
    const [inputType, setInputType] = useState('ingredients');
    const [ingredientsInput, setIngredientsInput] = useState('');
    const [recipeNameInput, setRecipeNameInput] = useState('');
    const [numServings, setNumServings] = useState(2);

    const [customPromptText, setCustomPromptText] = useState('');
    const [dietPreference, setDietPreference] = useState('');
    const [otherDietOrCuisine, setOtherDietOrCuisine] = useState('');
    const [avoidIngredientInput, setAvoidIngredientInput] = useState('');
    const [ingredientsToAvoidList, setIngredientsToAvoidList] = useState([]);
    const avoidInputRef = useRef(null);

    const [generatedRecipeAlternatives, setGeneratedRecipeAlternatives] = useState([]);
    const [generatedRecipeAlternativeImages, setGeneratedRecipeAlternativeImages] = useState([]);
    const [selectedRecipeData, setSelectedRecipeData] = useState(null);
    const [selectedRecipeImageUrl, setSelectedRecipeImageUrl] = useState('');

    const [isLoading, setIsLoading] = useState(false); // Para la generación principal de alternativas
    const [isConfirmingSelection, setIsConfirmingSelection] = useState(false); // Para la acción de confirmar una alternativa
    const [error, setError] = useState('');
    const [isRecipeSavedAndVisible, setIsRecipeSavedAndVisible] = useState(false);

    const [recipeHistory, setRecipeHistory] = useState([]);
    const [isLoadingHistory, setIsLoadingHistory] = useState(false);
    const [historyError, setHistoryError] = useState('');
    const [selectedHistoryRecipe, setSelectedHistoryRecipe] = useState(null);
    const [showHistoryModal, setShowHistoryModal] = useState(false);

    const dietAndCuisineOptions = useMemo(() => [
        { value: '', label: 'Any / Default Healthy' }, { type: 'group', label: 'Dietary Styles' },
        { value: 'Mediterranean', label: 'Mediterranean' }, { value: 'Vegetarian', label: 'Vegetarian' },
        { value: 'Vegan', label: 'Vegan' }, { value: 'Keto', label: 'Keto / Low-Carb' },
        { value: 'Paleo', label: 'Paleo' }, { value: 'Gluten-Free', label: 'Gluten-Free' },
        { value: 'Dairy-Free', label: 'Dairy-Free' }, { value: 'Low-FODMAP', label: 'Low-FODMAP' },
        { type: 'group', label: 'Regional Cuisines' }, { value: 'Italian', label: 'Italian Inspired' },
        { value: 'Mexican', label: 'Mexican Inspired' }, { value: 'Indian', label: 'Indian Inspired' },
        { value: 'Chinese', label: 'Chinese Inspired' }, { value: 'Thai', label: 'Thai Inspired' },
        { value: 'Japanese', label: 'Japanese Inspired' }, { value: 'French', label: 'French Inspired' },
        { value: 'other', label: 'Other (Specify Diet/Cuisine)' },
    ], []);

    const placeholderExamples = useMemo(() => ["e.g., make it vegan", "e.g., low-carb version", "e.g., ready in 30 minutes", "e.g., use air fryer for chicken"], []);
    const [placeholderText, setPlaceholderText] = useState(placeholderExamples[0]);

    useEffect(() => {
        const id = setInterval(() => {
            setPlaceholderText(prev => placeholderExamples[(placeholderExamples.indexOf(prev) + 1) % placeholderExamples.length]);
        }, 3500);
        return () => clearInterval(id);
    }, [placeholderExamples]);


    const fetchAndSetHistory = useCallback(async () => {
        if (userId) {
            setIsLoadingHistory(true); setHistoryError('');
            try {
                const history = await getRecipeHistory(userId);
                setRecipeHistory(history || []);
            } catch (err) {
                setHistoryError('Could not load recipe history.'); console.error(err);
            } finally { setIsLoadingHistory(false); }
        }
    }, [userId]);

    useEffect(() => { if (viewMode === 'history') { fetchAndSetHistory(); } }, [viewMode, fetchAndSetHistory]);

    useEffect(() => {
        if (inputType === 'ingredients') setRecipeNameInput('');
        else setIngredientsInput('');
        setGeneratedRecipeAlternatives([]);
        setGeneratedRecipeAlternativeImages([]);
        setSelectedRecipeData(null);
        setSelectedRecipeImageUrl('');
        setIsRecipeSavedAndVisible(false);
        setError('');
    }, [inputType]);


    const handleAddAvoidIngredient = useCallback(() => {
        const ingredient = avoidIngredientInput.trim().toLowerCase();
        if (ingredient && !ingredientsToAvoidList.includes(ingredient)) {
            setIngredientsToAvoidList(prev => [...prev, ingredient]);
        }
        setAvoidIngredientInput('');
        if (avoidInputRef.current) avoidInputRef.current.focus();
    }, [avoidIngredientInput, ingredientsToAvoidList]);

    const handleRemoveAvoidIngredient = useCallback((ingredientToRemove) => {
        setIngredientsToAvoidList(prev => prev.filter(ing => ing !== ingredientToRemove));
    }, []);

    const effectiveOverallRestrictions = useMemo(() => {
        const profileRest = Array.isArray(userRestrictions)
            ? userRestrictions.map(r => (typeof r === 'string' ? r.trim().toLowerCase() : (r?.restriction || '').trim().toLowerCase())).filter(Boolean)
            : [];
        const localAvoids = ingredientsToAvoidList.map(r => r.trim().toLowerCase()).filter(Boolean);
        return [...new Set([...profileRest, ...localAvoids])];
    }, [userRestrictions, ingredientsToAvoidList]);

    const handleGenerateRecipe = useCallback(async (options = {}) => {
        const {
            recipeIdea = recipeNameInput,
            targetCalories,
            targetMacros,
            servings = numServings,
            currentDietPreference = dietPreference,
            currentOtherDietOrCuisine = otherDietOrCuisine,
            currentCustomPromptText = customPromptText,
            currentIngredientsToAvoid = ingredientsToAvoidList,
            currentInputType = inputType,
            currentIngredientsValue = ingredientsInput,
        } = options;

        if (currentInputType === 'ingredients' && !currentIngredientsValue.trim() && !options.recipeIdea) { setError('Please provide some ingredients.'); return null; }
        if (currentInputType === 'recipeName' && !recipeIdea.trim()) { setError('Please enter a recipe name or idea.'); return null; }
        if (currentDietPreference === 'other' && !currentOtherDietOrCuisine.trim()) { setError('Please specify the "Other" diet/cuisine.'); return null; }

        setIsLoading(true); setError('');
        setGeneratedRecipeAlternatives([]);
        setGeneratedRecipeAlternativeImages([]);
        setSelectedRecipeData(null);
        setSelectedRecipeImageUrl('');
        setIsRecipeSavedAndVisible(false);

        let baseQuery = '';
        if (currentInputType === 'ingredients' && currentIngredientsValue.trim()) {
            baseQuery = `recipes using these ingredients: ${currentIngredientsValue}.`;
        } else {
            baseQuery = `recipes for "${recipeIdea}".`;
        }

        let fullQuery = `Generate 3 distinct recipe alternatives for ${baseQuery}`;
        const finalDietOrCuisine = currentDietPreference === 'other' ? currentOtherDietOrCuisine.trim() : currentDietPreference;
        if (finalDietOrCuisine) fullQuery += ` Align with ${finalDietOrCuisine} style.`;
        else fullQuery += ` Generally healthy and culturally diverse.`;

        fullQuery += ` Each for ${servings > 0 ? servings : 2} serving${(servings || 2) !== 1 ? 's' : ''}.`;
        if (targetCalories) fullQuery += ` The target calories per serving for each recipe is approximately ${targetCalories} kcal.`;
        if (targetMacros) fullQuery += ` The target macronutrient breakdown per serving for each should be around Protein: ${targetMacros.protein}g, Carbs: ${targetMacros.carbs}g, Fat: ${targetMacros.fat}g.`;
        if (currentCustomPromptText.trim()) fullQuery += ` Additional Instructions for all: ${currentCustomPromptText.trim()}.`;

        let goalContext = '';
        if (Array.isArray(userHealthGoals) && userHealthGoals.length > 0) {
            goalContext = 'User Health Goals (for context):\n' + userHealthGoals.map(g => `- ${g.target}${g.deadline ? ` (by ${g.deadline})` : ''}`).join('\n') + '\n\n';
        }

        const allRestrictionsForThisCall = [...new Set([
            ...(Array.isArray(userRestrictions) ? userRestrictions.map(r => (typeof r === 'string' ? r : r?.restriction).trim().toLowerCase()) : []),
            ...currentIngredientsToAvoid.map(r => r.trim().toLowerCase())
        ])].filter(Boolean);

        let restrictionContext = '';
        if (allRestrictionsForThisCall.length > 0) {
            restrictionContext = 'Strictly AVOID these ingredients/foods in all recipes: ' + allRestrictionsForThisCall.join(", ") + '.\n\n';
        }

        const systemPrompt = `You are a highly skilled cooking assistant and creative nutritionist.
${goalContext}${restrictionContext}User Request Overview: ${fullQuery}

Output Format: Return ONLY a valid JSON object with the following structure:
{ 
  "alternatives": [
    { 
      "recipeName": "string (A creative and fitting name)", 
      "description": "string (1-2 sentences, highlight what makes this alternative unique)",
      "prepTime": "string (e.g., '15 minutes')",
      "cookTime": "string (e.g., '25 minutes')", 
      "estimatedCaloriesPerServing": ${targetCalories || "number (e.g., 485, BE ACCURATE)"},
      "macrosPerServing": { "protein": ${targetMacros?.protein || "number"}, "carbs": ${targetMacros?.carbs || "number"}, "fat": ${targetMacros?.fat || "number"} },
      "macrosPer100g": { "protein": "number", "carbs": "number", "fat": "number" },
      "cuisineType": "string (e.g., Italian)",
      "ingredients": ["string (e.g., '1 large chicken breast (approx. 200g), cubed')"], 
      "steps": ["string (Concise and clear)"], 
      "notes": ["string (Optional: Chef's Tips, variations)"] 
    } 
    // ... (exactly 3 such recipe objects in this array)
  ]
}
CRITICAL:
- Provide EXACTLY 3 distinct recipe alternatives in the "alternatives" array.
- Each recipe object must adhere to the specified structure.
- "estimatedCaloriesPerServing" MUST be a number.
- "macrosPerServing" (protein, carbs, fat in grams) MUST be numbers.
- Adapt ingredient amounts for the specified number of servings.
Return ONLY JSON.`;

        try {
            const aiResponse = await callPollinationsTextApi(systemPrompt, "Generate 3 recipe alternatives.");

            if (aiResponse && Array.isArray(aiResponse.alternatives) && aiResponse.alternatives.length > 0) {
                const validAlternatives = aiResponse.alternatives.filter(
                    alt => alt.recipeName && typeof alt.estimatedCaloriesPerServing === 'number' && alt.macrosPerServing
                ).slice(0, 3);

                if (validAlternatives.length === 0) {
                    throw new Error('AI returned alternatives but none were valid or complete.');
                }

                setGeneratedRecipeAlternatives(validAlternatives.map(alt => ({ ...alt, servings: servings })));

                const images = validAlternatives.map(alt => {
                    const imagePromptText = `A delicious, appetizing plate of ${alt.recipeName}, ${alt.cuisineType || ''} style, ${alt.description || ''}, realistic food photography, high detail, gourmet presentation`;
                    return `${IMAGE_API_BASE_POLLINATIONS}${encodeURIComponent(imagePromptText)}?width=512&height=300&model=${DEFAULT_IMAGE_MODEL}&nologo=true&seed=${Date.now()}${Math.random()}`;
                });
                setGeneratedRecipeAlternativeImages(images);

                if (options.isProgrammaticCall) {
                    setIsLoading(false);
                    return { ...validAlternatives[0], servings: servings };
                }
            } else {
                console.error("Generated recipes missing key 'alternatives' array or invalid format:", aiResponse);
                throw new Error('Generated recipes in unexpected format or missing alternatives.');
            }
        } catch (fetchError) {
            const errMsg = `Failed to generate recipe alternatives: ${fetchError.message}.`;
            console.error(errMsg, fetchError);
            setError(errMsg);
            setGeneratedRecipeAlternatives([]);
            setGeneratedRecipeAlternativeImages([]);
        } finally {
            if (!options.isProgrammaticCall) {
                setIsLoading(false);
            }
        }
    }, [
        ingredientsInput, recipeNameInput, customPromptText, userRestrictions, userHealthGoals,
        inputType, dietPreference, otherDietOrCuisine, ingredientsToAvoidList, numServings,
    ]);

    const handleViewAlternativeDetails = useCallback((recipe, imageUrl) => {
        setSelectedRecipeData(recipe);
        setSelectedRecipeImageUrl(imageUrl);
        setError('');
    }, []);

    const handleConfirmSelection = useCallback(async () => {
        if (!selectedRecipeData) return;

        setIsConfirmingSelection(true);
        setError('');
        try {
            let recipeToSave = { ...selectedRecipeData };
            if (onRecipeGenerated && typeof onRecipeGenerated === 'function') {
                const savedActivity = await onRecipeGenerated(recipeToSave, false);
                recipeToSave.id = savedActivity?.id || null;
            } else {
                recipeToSave.id = null;
            }
            setSelectedRecipeData(recipeToSave);

            setGeneratedRecipeAlternatives([]);
            setGeneratedRecipeAlternativeImages([]);
            setIsRecipeSavedAndVisible(false);
        } catch (e) {
            console.error("Error confirming selected alternative:", e);
            setError("Could not confirm your selection. Please try again.");
            setGeneratedRecipeAlternatives([]);
            setGeneratedRecipeAlternativeImages([]);
        } finally {
            setIsConfirmingSelection(false);
        }
    }, [selectedRecipeData, onRecipeGenerated]);

    const handleReturnToAlternatives = useCallback(() => {
        setSelectedRecipeData(null);
        setSelectedRecipeImageUrl('');
        setError('');
    }, []);


    const handleMakeRecipeVisible = async () => {
        if (selectedRecipeData && selectedRecipeData.id && userId) {
            setIsLoading(true);
            setError('');
            try {
                const success = await visibleUserActivity(selectedRecipeData.id, userId);
                if (success) {
                    setIsRecipeSavedAndVisible(true);
                    setSelectedRecipeData(prev => ({ ...prev, isVisible: true }));
                    fetchAndSetHistory();
                } else { setError("Could not mark recipe as visible."); }
            } catch (err) {
                setError("An error occurred while saving the recipe."); console.error(err);
            } finally { setIsLoading(false); }
        } else { setError("Cannot save recipe: data or ID is missing, or not selected."); }
    };

    const handleShowGeneratorView = () => {
        setGeneratedRecipeAlternatives([]); setGeneratedRecipeAlternativeImages([]);
        setSelectedRecipeData(null); setSelectedRecipeImageUrl('');
        setError(''); setIsRecipeSavedAndVisible(false);
        setInputType('ingredients'); setIngredientsInput(''); setRecipeNameInput('');
        setCustomPromptText(''); setDietPreference(''); setOtherDietOrCuisine('');
        setIngredientsToAvoidList([]); setNumServings(2);
        setViewMode('generator');
    };

    const handleShowHistoryView = () => { setViewMode('history'); };
    const handleShowHistoryDetails = (recipe) => { setSelectedHistoryRecipe(recipe); setShowHistoryModal(true); };
    const handleCloseHistoryModal = () => { setSelectedHistoryRecipe(null); setShowHistoryModal(false); };

    const handleDeleteRecipeFromHistory = async (recipeIdToHide, e) => {
        e.stopPropagation();
        if (!recipeIdToHide || !userId) { setHistoryError("Cannot hide recipe: missing ID or user info."); return; }
        if (window.confirm("Are you sure you want to hide this recipe from your history?")) {
            setIsLoadingHistory(true);
            try {
                const success = await hideUserActivity(recipeIdToHide, userId);
                if (success) {
                    setRecipeHistory(prevHistory => prevHistory.filter(recipe => recipe.id !== recipeIdToHide));
                    setHistoryError('');
                    if (selectedRecipeData && selectedRecipeData.id === recipeIdToHide) {
                        setIsRecipeSavedAndVisible(false);
                        // Keep selectedRecipeData but mark as not visible, ID is still there if they "re-save" from a different flow
                        setSelectedRecipeData(prev => ({ ...prev, isVisible: false }));
                    }
                } else { setHistoryError("Failed to hide recipe."); }
            } catch (err) {
                setHistoryError("An error occurred while hiding the recipe."); console.error(err);
            } finally { setIsLoadingHistory(false); }
        }
    };

    const renderSaveButton = () => (
        <OverlayTrigger placement="top" overlay={<Tooltip id="tooltip-save-recipe">Save this recipe to your visible history.</Tooltip>}>
            <Button
                variant="success" size="lg" className="save-recipe-button shadow-sm"
                onClick={handleMakeRecipeVisible}
                disabled={isLoading || isRecipeSavedAndVisible || !selectedRecipeData?.id}
            >
                <FontAwesomeIcon icon={isRecipeSavedAndVisible ? faCheckCircle : faSave} className="me-2 button-icon-pulse" />
                {isLoading && !isRecipeSavedAndVisible && selectedRecipeData?.id ? <><Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />Saving...</> : (isRecipeSavedAndVisible ? "Saved!" : "Save to My Recipes")}
            </Button>
        </OverlayTrigger>
    );

    const renderGeneratorForm = () => (
        <>
            <Form onSubmit={(e) => { e.preventDefault(); handleGenerateRecipe(); }}>
                <Form.Group className="mb-4 text-center">
                    <Form.Label className="fw-bold d-block mb-2 fs-5 text-secondary">Generate recipe alternatives based on:</Form.Label>
                    <ToggleButtonGroup type="radio" name="inputType" value={inputType} onChange={(val) => { setInputType(val); }} className="d-inline-flex shadow-sm rounded-pill">
                        <ToggleButton id="radio-ingredients" value="ingredients" variant={inputType === 'ingredients' ? 'primary' : 'outline-primary'} className="rounded-start-pill px-4 py-2">Ingredients</ToggleButton>
                        <ToggleButton id="radio-recipeName" value="recipeName" variant={inputType === 'recipeName' ? 'primary' : 'outline-primary'} className="rounded-end-pill px-4 py-2">Recipe Name</ToggleButton>
                    </ToggleButtonGroup>
                </Form.Group>

                {inputType === 'ingredients' && (
                    <Form.Group className="mb-3" controlId="ingredientsList">
                        <Form.Label className="fw-medium">Key Ingredients (comma-separated):</Form.Label>
                        <Form.Control as="textarea" rows={3} placeholder="e.g., chicken breast, broccoli, olive oil, lemon" value={ingredientsInput} onChange={(e) => setIngredientsInput(e.target.value)} disabled={isLoading || !!selectedRecipeData} autoFocus />
                    </Form.Group>
                )}
                {inputType === 'recipeName' && (
                    <Form.Group className="mb-3" controlId="recipeName">
                        <Form.Label className="fw-medium">Recipe Name or General Idea:</Form.Label>
                        <Form.Control type="text" placeholder="e.g., Quick Chicken Stir-fry, Healthy Salmon Dinner" value={recipeNameInput} onChange={(e) => setRecipeNameInput(e.target.value)} disabled={isLoading || !!selectedRecipeData} autoFocus />
                    </Form.Group>
                )}

                <PreferenceForm
                    dietPreference={dietPreference} onDietPreferenceChange={setDietPreference}
                    otherDietOrCuisine={otherDietOrCuisine} onOtherDietOrCuisineChange={setOtherDietOrCuisine}
                    avoidIngredientInput={avoidIngredientInput} onAvoidIngredientInputChange={setAvoidIngredientInput}
                    ingredientsToAvoidList={ingredientsToAvoidList} onAddAvoidIngredient={handleAddAvoidIngredient}
                    onRemoveAvoidIngredient={handleRemoveAvoidIngredient}
                    customPromptText={customPromptText} onCustomPromptTextChange={setCustomPromptText}
                    dietAndCuisineOptions={dietAndCuisineOptions} userRestrictions={userRestrictions}
                    isLoading={isLoading || !!selectedRecipeData} avoidInputRef={avoidInputRef}
                />
                <Card className="mb-4 p-3 bg-light border shadow-sm">
                    <Row>
                        <Col md={6}>
                            <Form.Group controlId="numServings">
                                <Form.Label><FontAwesomeIcon icon={faUsers} className="me-1" /> Number of Servings (for each alternative):</Form.Label>
                                <Form.Control type="number" min="1" max="12" value={numServings} onChange={(e) => setNumServings(Math.max(1, parseInt(e.target.value, 10) || 1))} disabled={isLoading || !!selectedRecipeData} />
                            </Form.Group>
                        </Col>
                    </Row>
                </Card>

                {(effectiveOverallRestrictions.length > 0 || (userHealthGoals && userHealthGoals.length > 0)) && (
                    <Row className="g-2 mb-3">
                        {effectiveOverallRestrictions.length > 0 && (
                            <Col xs={12} md={(userHealthGoals && userHealthGoals.length > 0) ? 6 : 12}>
                                <Alert variant="light" className="p-2 small border text-info mb-0 h-100 shadow-sm">
                                    <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
                                    Using combined restrictions: <strong>{effectiveOverallRestrictions.join(', ')}</strong>
                                </Alert>
                            </Col>
                        )}
                        {(userHealthGoals && userHealthGoals.length > 0) && (
                            <Col xs={12} md={(effectiveOverallRestrictions.length > 0) ? 6 : 12}>
                                <Alert variant="light" className="p-2 small border text-info mb-0 h-100 shadow-sm">
                                    <FontAwesomeIcon icon={faBullseye} className="me-2" />
                                    Using profile goals: <strong>{userHealthGoals.map(g => g.target).join(', ')}</strong>
                                </Alert>
                            </Col>
                        )}
                    </Row>
                )}

                <div className="text-center">
                    <Button type="submit" variant="success" disabled={isLoading || !!selectedRecipeData} size="lg" className="shadow-sm">
                        {isLoading && !selectedRecipeData ? <><Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />Generating Alternatives...</> : <><FontAwesomeIcon icon={faLightbulb} className="me-2" /> Get Recipe Ideas</>}
                    </Button>
                </div>
            </Form>

            {error && <Alert variant="danger" className="mt-4 shadow-sm">{error}</Alert>}

            {/* ESTADO 1: Mostrando tarjetas de alternativas */}
            {!isLoading && generatedRecipeAlternatives.length > 0 && !selectedRecipeData && (
                <div className="mt-4">
                    <h3 className="text-center mb-3">Choose a Recipe Alternative:</h3>
                    <Row xs={1} md={2} lg={3} className="g-4">
                        {generatedRecipeAlternatives.map((recipe, index) => (
                            <Col key={index}>
                                <Card className="h-100 shadow-sm recipe-alternative-card" onClick={() => handleViewAlternativeDetails(recipe, generatedRecipeAlternativeImages[index])} style={{ cursor: 'pointer' }}>
                                    {generatedRecipeAlternativeImages[index] && (
                                        <CardImg top src={generatedRecipeAlternativeImages[index]} alt={`Image for ${recipe.recipeName}`} style={{ height: '200px', objectFit: 'cover' }} />
                                    )}
                                    <Card.Body className="d-flex flex-column">
                                        <Card.Title className="h5">{recipe.recipeName}</Card.Title>
                                        <Card.Text className="small flex-grow-1">
                                            {recipe.description?.substring(0, 100)}{recipe.description?.length > 100 ? '...' : ''}
                                        </Card.Text>
                                        <div className="mt-auto">
                                            <Badge bg="info" className="me-2">{recipe.estimatedCaloriesPerServing} Kcal</Badge>
                                            <Badge bg="secondary">{recipe.cuisineType || 'General'}</Badge>
                                        </div>
                                    </Card.Body>
                                    <Card.Footer className="text-center bg-light">
                                        <FontAwesomeIcon icon={faEye} className="me-2" /> View Details
                                    </Card.Footer>
                                </Card>
                            </Col>
                        ))}
                    </Row>
                </div>
            )}

            {/* ESTADO 2: Previsualizando una alternativa seleccionada */}
            {!isLoading && selectedRecipeData && generatedRecipeAlternatives.length > 0 && (
                <>
                    <Card className="mt-4 shadow">
                        <Card.Header className="bg-light p-3">
                            <div className="d-flex justify-content-between align-items-center">
                                <h4 className="mb-0 me-2"> {selectedRecipeData.recipeName}</h4>
                                <div>
                                    <Button variant="outline-secondary" onClick={handleReturnToAlternatives} className="mx-2" disabled={isConfirmingSelection}>
                                        <FontAwesomeIcon icon={faArrowLeft} className="me-1" /> Back to Alternatives
                                    </Button>
                                    <Button variant="primary" onClick={handleConfirmSelection} disabled={isConfirmingSelection}>
                                        {isConfirmingSelection ?
                                            <><Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />Confirming...</> :
                                            <><FontAwesomeIcon icon={faCheckCircle} className="me-1" /> Choose This Recipe</>
                                        }
                                    </Button>
                                </div>
                            </div>
                        </Card.Header>
                        <Card.Body>
                            <RecipeDisplay
                                recipeData={selectedRecipeData}
                                imageUrlOverride={selectedRecipeImageUrl}
                                isNewlyGenerated={true}
                            />
                        </Card.Body>
                    </Card>
                </>
            )}

            {/* ESTADO 3: Receta confirmada, lista para guardar visiblemente o ya guardada */}
            {!isLoading && selectedRecipeData && generatedRecipeAlternatives.length === 0 && (
                <>
                    <Card className="mt-4 shadow">
                        {!isRecipeSavedAndVisible && selectedRecipeData.id && (
                            <Card.Header className="bg-light p-3 text-center">{renderSaveButton()}</Card.Header>
                        )}
                        {isRecipeSavedAndVisible && (
                            <Card.Header className="bg-light-success p-3 text-center text-success">
                                <FontAwesomeIcon icon={faCheckCircle} className="me-2" /> Recipe Saved to My Collection!
                            </Card.Header>
                        )}
                        {!selectedRecipeData.id && !isRecipeSavedAndVisible && !isConfirmingSelection && (
                            <Card.Header className="bg-light p-3 text-center">
                                <Alert variant="info" className="mb-0">
                                    <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
                                    This recipe is selected.
                                    {onRecipeGenerated ? " You can save it to your collection using the button above once confirmed." : "Saving to history is not configured for this setup."}
                                </Alert>
                            </Card.Header>
                        )}
                        <Card.Body>
                            <h2 className="mb-3 text-center">Selected Recipe: {selectedRecipeData.recipeName}</h2>
                            <RecipeDisplay
                                recipeData={selectedRecipeData}
                                imageUrlOverride={selectedRecipeImageUrl}
                                isNewlyGenerated={!selectedRecipeData.id || !isRecipeSavedAndVisible}
                            />
                        </Card.Body>
                    </Card>
                </>
            )}
        </>
    );

    const renderRecipeHistoryItemSummary = (recipe) => (
        <>
            <div className="fw-bold">{recipe.recipeName || "Untitled Recipe"}</div>
            <span className="text-muted small">
                {recipe.description ? (recipe.description.length > 100 ? recipe.description.substring(0, 100) + "..." : recipe.description) : "No description available."}
            </span>
            {recipe.cuisineType && <Badge bg="light" text="dark" className="ms-2">{recipe.cuisineType}</Badge>}
        </>
    );

    return (
        <>
            <ViewModeLayout
                viewMode={viewMode}
                onShowGeneratorView={handleShowGeneratorView}
                onShowHistoryView={handleShowHistoryView}
                historyTitle="My Recipe Collection"
                generatorTitle="AI Recipe Chef"
                historyIcon={faHistory}
                generatorIcon={faUtensils}
                newItemButtonText="New Recipe Idea"
            >
                {viewMode === 'history' ? (
                    <HistoryView
                        items={recipeHistory} isLoading={isLoadingHistory} error={historyError}
                        onSelectItem={handleShowHistoryDetails} onHideItem={handleDeleteRecipeFromHistory}
                        renderItemSummary={renderRecipeHistoryItemSummary} searchPlaceholder="Search my recipes..."
                        itemTypePlural="Recipes"
                        filterFunction={(items, term) =>
                            items.filter(recipe =>
                                (recipe.recipeName && recipe.recipeName.toLowerCase().includes(term)) ||
                                (recipe.description && recipe.description.toLowerCase().includes(term)) ||
                                (recipe.cuisineType && recipe.cuisineType.toLowerCase().includes(term)) ||
                                (Array.isArray(recipe.ingredients) && recipe.ingredients.some(ing => ing.toLowerCase().includes(term)))
                            )
                        }
                    />
                ) : (
                    renderGeneratorForm()
                )}
            </ViewModeLayout>

            <ItemDetailsModal
                show={showHistoryModal} onHide={handleCloseHistoryModal}
                itemData={selectedHistoryRecipe}
                renderItemDetailsContent={(item) => <RecipeDisplay recipeData={item} isNewlyGenerated={false} />}
                modalTitle={selectedHistoryRecipe?.recipeName || "Recipe Details"}
            />
            <style jsx global>{`
                .save-recipe-button { transition: all 0.3s ease-in-out; font-weight: 500; }
                .save-recipe-button:hover { transform: translateY(-2px); box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15); }
                .save-recipe-button:active { transform: translateY(0px); box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1); }
                .button-icon-pulse { animation: pulseEffect 1.5s infinite ease-in-out; }
                @keyframes pulseEffect { 0% { transform: scale(1); } 50% { transform: scale(1.1); } 100% { transform: scale(1); } }
                .bg-light-success { background-color: #d1e7dd !important; }
                .badge-remove-btn:hover { color: var(--bs-danger) !important; }
                .border-bottom-dashed { border-bottom: 1px dashed #e0e0e0 !important; }
                .border-bottom-dashed:last-child { border-bottom: none !important; }
                .recipe-image-col img { transition: transform 0.3s ease-in-out; }
                .recipe-image-col:hover img { transform: scale(1.03); }
                .recipe-alternative-card:hover {
                    transform: translateY(-5px);
                    box-shadow: 0 8px 16px rgba(0,0,0,0.1);
                    transition: transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out;
                }
            `}</style>
        </>
    );
};

export default RecipeGenerator;